import { ApiFactoryClients } from "../api.types";
import {
  TakedownAction,
  TakedownPracticeRow,
  DeindexResult,
  ChannelEffectiveness,
  EmailCollectionStatus,
  AutoPolicy,
  UserRemoval,
  ReplyRecord,
  DeindexSubmission,
  RouteSummary,
} from "core/types/takedown.types";

// eslint-disable-next-line import/no-anonymous-default-export
export default ({ codeHttpClient }: ApiFactoryClients<"codeHttpClient">) => ({
  // Lista grezza di TUTTE le pratiche legali (con takedown_summary, user, urls).
  // NON passa per i transformer redux delle practices (che droppano takedown_summary).
  listPractices: (): Promise<TakedownPracticeRow[]> =>
    codeHttpClient
      .get("/admin/legalPractices")
      .then(({ data }) => data as TakedownPracticeRow[]),
  getActions: (practiceId: number): Promise<TakedownAction[]> =>
    codeHttpClient
      .get(`/admin/takedown?practice_id=${practiceId}`)
      .then(({ data }) => data as TakedownAction[]),
  prepare: (practiceId: number, contentType = "general", whitelist?: string[]) =>
    codeHttpClient.post("/admin/takedown/prepare", {
      practice_id: practiceId,
      content_type: contentType,
      whitelist,
    }),
  // Ripulisci e ri-prepara: cancella le azioni 'prepared' vecchie e rigenera col codice corrente
  // (destinatari puliti, nome cliente, registrar). Approvate/inviate restano intatte.
  reprepare: (practiceId: number, contentType = "general", whitelist?: string[]) =>
    codeHttpClient.post("/admin/takedown/reprepare", {
      practice_id: practiceId,
      content_type: contentType,
      whitelist,
    }),
  approve: (actionIds: number[]) =>
    codeHttpClient.post("/admin/takedown/approve", { action_ids: actionIds }),
  reject: (actionIds: number[]) =>
    codeHttpClient.post("/admin/takedown/reject", { action_ids: actionIds }),
  send: (practiceId: number) =>
    codeHttpClient.post("/admin/takedown/send", { practice_id: practiceId }),
  // Risposte dei provider che richiedono un'azione umana (pannello "Azioni richieste")
  // practiceId omesso ⇒ inbox globale (backend: get_replies(practice_id=None) → tutte le pratiche).
  getReplies: (practiceId?: number): Promise<{ replies: ReplyRecord[] }> =>
    codeHttpClient
      .get(`/admin/takedown/replies${practiceId ? `?practice_id=${practiceId}` : ""}`)
      .then(({ data }) => data as { replies: ReplyRecord[] }),
  // Dossier abuse pre-compilato per un'azione+provider (form_url + campi + checklist)
  getFormDossier: (actionId: number, provider: string): Promise<{ dossier: any }> =>
    codeHttpClient
      .get(`/admin/takedown/form-dossier?action_id=${actionId}&provider=${encodeURIComponent(provider)}`)
      .then(({ data }) => data as { dossier: any }),
  // Segna un form come consegnato a mano (con eventuale case id del provider)
  markFormSubmitted: (actionId: number, providerCaseRef?: string) =>
    codeHttpClient.post("/admin/takedown/form-submitted", {
      action_id: actionId,
      provider_case_ref: providerCaseRef,
    }),
  // Deindex: dossier pre-compilati (link form + testo da incollare) per gli URL della pratica.
  getDeindex: (
    practiceId: number,
    contentType = "general",
    eu = true,
    holderName?: string,
    originalWorkUrl?: string
  ): Promise<DeindexResult> => {
    const q = new URLSearchParams({
      practice_id: String(practiceId),
      content_type: contentType,
      eu: eu ? "1" : "0",
    });
    if (holderName) q.set("holder_name", holderName);
    if (originalWorkUrl) q.set("original_work_url", originalWorkUrl);
    return codeHttpClient
      .get(`/admin/takedown/deindex?${q.toString()}`)
      .then(({ data }) => data as DeindexResult);
  },
  // Segna come inviata la richiesta di deindicizzazione a un motore. L'invio ai motori resta
  // manuale (i loro form hanno CAPTCHA): qui si registra solo che e' stato fatto, altrimenti
  // nessuno sa piu' quali pratiche siano gia' state deindicizzate.
  markDeindexSubmitted: (
    practiceId: number,
    engine: string,
    opts?: { urlsCount?: number; contentType?: string; providerCaseRef?: string; notes?: string }
  ): Promise<{ submissions: DeindexSubmission[] }> =>
    codeHttpClient
      .post("/admin/takedown/deindex-submitted", {
        practice_id: practiceId,
        engine,
        urls_count: opts?.urlsCount,
        content_type: opts?.contentType,
        provider_case_ref: opts?.providerCaseRef,
        notes: opts?.notes,
      })
      .then(({ data }) => data as { submissions: DeindexSubmission[] }),
  // Conteggi della pratica separati per rotta (diffidabili vs sola deindicizzazione).
  getRouteSummary: (practiceId: number): Promise<RouteSummary> =>
    codeHttpClient
      .get(`/admin/takedown/route-summary?practice_id=${practiceId}`)
      .then(({ data }) => data as RouteSummary),
  // Stato LIVE della raccolta email (finder) per la pratica.
  getEmailStatus: (practiceId: number): Promise<EmailCollectionStatus> =>
    codeHttpClient
      .get(`/admin/takedown/email-status?practice_id=${practiceId}`)
      .then(({ data }) => data as EmailCollectionStatus),
  // Efficacia per-canale (quali canali ottengono rimozioni).
  getEffectiveness: (): Promise<{ channels: ChannelEffectiveness[] }> =>
    codeHttpClient
      .get("/admin/takedown/effectiveness")
      .then(({ data }) => data as { channels: ChannelEffectiveness[] }),

  // ── Automazione (G1/G2) ────────────────────────────────────────────────
  // Policy auto-select / auto-approve. GET/PUT /admin/takedown/policy (contract §5.3/§6.4).
  getPolicy: (): Promise<AutoPolicy> =>
    codeHttpClient.get("/admin/takedown/policy").then(({ data }) => data as AutoPolicy),

  updatePolicy: (patch: Partial<AutoPolicy>): Promise<AutoPolicy> =>
    codeHttpClient
      .put("/admin/takedown/policy", patch)
      .then(({ data }) => data as AutoPolicy),

  // ── Vista end-user "le tue rimozioni" (simple_auth) — GET /account/removals (§5.3/§6.5).
  // Risposta reale (Account.get_removals): { "removals": UserRemoval[] } — non un array nudo.
  getRemovals: (): Promise<UserRemoval[]> =>
    codeHttpClient
      .get("/account/removals")
      .then(({ data }) => (data?.removals ?? []) as UserRemoval[]),
});
